import { ChangeEvent, FC } from "react";
import { Box, Typography } from "@material-ui/core";
import { FormikBag, FormikProps, withFormik } from "formik";
import * as yup from "yup";

import {
  AllAccessDiscountTooltip,
  Button,
  LabelField,
  MarketplaceMedianLabelField,
  NumberField,
  ROIEquation,
  UsdIntegerInput,
} from "components";
import { getReturnOnInvestment } from "lib/finance";
import { formatCurrencyAmount, formatPercentage } from "lib/format";
import { parseNumber } from "lib/parse";
import {
  BaseAmountSource,
  ListingKind,
  ResaleInfo,
} from "services/listings/types";

export interface FormValues {
  saleProceeds: string;
  buyNowPrice: string;
  royxTakeAmount: string;
}

interface FormProps {
  onSave: (values: FormValues) => Promise<void>;
  highestOpenBuyerOfferAmount: number;
  initialValues: FormValues;
  isAcceptingFinalOffersEnabled: boolean;
  isMarketplaceMedianDisabled: boolean;
  listingKind: ListingKind;
  marketplaceMedian: string | null;
  marketplaceMedianMultiplier: number;
  marketplaceMedianSource: BaseAmountSource;
  minimumPrice: number;
  resaleInfo: ResaleInfo | null;
  royxTake: number;
}

const SetBuyNowPriceForm: FC<FormProps & FormikProps<FormValues>> = ({
  values,
  errors,
  touched,
  handleBlur,
  handleSubmit,
  setFieldValue,
  isSubmitting,
  isAcceptingFinalOffersEnabled,
  isMarketplaceMedianDisabled,
  listingKind,
  marketplaceMedian,
  marketplaceMedianMultiplier,
  marketplaceMedianSource,
  resaleInfo,
  royxTake,
}) => {
  const handleSetBuyNowPrice = (event: ChangeEvent<HTMLInputElement>) => {
    const buyNowPrice = parseNumber(event.target.value, 2);
    const royxTakeAmount = buyNowPrice * royxTake;
    const saleProceeds = buyNowPrice * (1 - royxTake);

    setFieldValue("buyNowPrice", event.target.value, false);
    setFieldValue("royxTakeAmount", String(royxTakeAmount), false);
    setFieldValue("saleProceeds", String(saleProceeds), false);
  };

  const saleProceeds = parseNumber(values.saleProceeds);
  const returnOnInvestment: number =
    !!saleProceeds && !!resaleInfo
      ? getReturnOnInvestment(
          resaleInfo.royalties_received,
          saleProceeds,
          resaleInfo.purchased_amount,
          resaleInfo.buyer_fee,
        )
      : null;

  return (
    <form onSubmit={handleSubmit}>
      <Typography component="div" gutterBottom>
        <Box fontSize={13}>
          {isAcceptingFinalOffersEnabled
            ? "If an investor accepts your list price, a 24-hour auction will begin immediately starting at the list price. The marketplace fee will be automatically subtracted from the highest bid at the end of the auction."
            : "Enter your list price below to make this listing available for immediate sale. The marketplace fee will be automatically subtracted from your list price."}
        </Box>
      </Typography>
      <Box my={2} textAlign="center">
        {!isMarketplaceMedianDisabled && (
          <Box my={2}>
            <MarketplaceMedianLabelField
              marketplaceMedian={marketplaceMedian}
              marketplaceMedianMultiplier={marketplaceMedianMultiplier}
              marketplaceMedianSource={marketplaceMedianSource}
            />
          </Box>
        )}
        <Box my={2}>
          <NumberField
            type="text"
            name="buyNowPrice"
            fullWidth
            label="List Price"
            value={values.buyNowPrice}
            helperText={touched.buyNowPrice && errors.buyNowPrice}
            disabled={isSubmitting}
            onBlur={handleBlur}
            onChange={handleSetBuyNowPrice}
            error={touched.buyNowPrice && !!errors.buyNowPrice}
            inputComponent={UsdIntegerInput}
            placeholder="$100,000"
          />
        </Box>
        <Box my={2}>
          <LabelField
            label={`\u2212 Marketplace Fee (${formatPercentage(royxTake)})`}
            value={
              isAcceptingFinalOffersEnabled
                ? `\u2212 ${formatPercentage(royxTake)}`
                : `\u2212${formatCurrencyAmount("USD", values.royxTakeAmount)}`
            }
            tooltipText={
              listingKind !== "direct_listing" ? (
                <AllAccessDiscountTooltip />
              ) : undefined
            }
            interactiveTooltip
          />
        </Box>
        <Box my={2}>
          <LabelField
            label={`= Your ${
              isAcceptingFinalOffersEnabled ? "Minimum " : ""
            }Sale Proceeds`}
            value={`${formatCurrencyAmount("USD", values.saleProceeds)}`}
          />
        </Box>
        {!!resaleInfo && !!resaleInfo?.last_update && (
          <Box my={2}>
            <LabelField
              label={`${
                isAcceptingFinalOffersEnabled ? "Minimum " : ""
              }Return On Investment`}
              tooltipText={<ROIEquation />}
              value={formatPercentage(returnOnInvestment)}
            />
          </Box>
        )}
        <Box my={2}>
          <Button
            customColor="green"
            variant="contained"
            size="large"
            id="btn-set-list-price-modal"
            type="submit"
            fullWidth
            disabled={isSubmitting}
          >
            Set List Price
          </Button>
        </Box>
      </Box>
    </form>
  );
};

const formikOptions = {
  mapPropsToValues: (props: FormProps) => props.initialValues,

  validationSchema: (props: FormProps) => {
    const { minimumPrice } = props;

    return yup.object().shape({
      buyNowPrice: yup
        .string()
        .required("Required")
        .test(
          "greaterThanListingMinimumPrice",
          `The list price must be greater than the minimum bid (${formatCurrencyAmount(
            "USD",
            minimumPrice,
          )})`,
          function (amount) {
            return amount && parseNumber(amount, 2) < minimumPrice
              ? false
              : true;
          },
        )
        .test(
          "greaterThanHighestOpenBuyerOffer",
          `The list price must be greater than the highest open offer of (${formatCurrencyAmount(
            "USD",
            props.highestOpenBuyerOfferAmount,
          )})`,
          function (amount) {
            return parseNumber(amount, 2) <= props.highestOpenBuyerOfferAmount
              ? false
              : true;
          },
        ),
    });
  },
  handleSubmit: async (
    values: FormValues,
    { props }: FormikBag<FormProps, FormValues>,
  ) => {
    try {
      await props.onSave(values);
    } catch {
      // pass
    }
  },
};

const EnhancedSetBuyNowPriceForm = withFormik<FormProps, FormValues>(
  formikOptions,
)(SetBuyNowPriceForm);

export default EnhancedSetBuyNowPriceForm;
